/**
 * The morph's **source surface**: the chat surface on `/chatbot`.
 *
 * `ChatBot` registers it itself (`registerAssistantSurface` in an effect) and
 * unregisters it on teardown. Deliberately no remote lookup via
 * `document.querySelector` from the navigator: there would then be two truths
 * about what "the chat surface" is, and the weaker one would be the one that
 * counts.
 *
 * Measuring happens **in the click handler**, in one go
 * (`measureAssistantSurface`) — at a moment when the surface is guaranteed to
 * be in the DOM and painted. Everything the overlay needs later is then held as
 * values in the morph record: rectangles, scroll position, fill color and a
 * **clone** of the subtree.
 *
 * Why a clone and not the real node: React removes the chat subtree in the
 * mutation phase of the same commit, so the node would be detached and
 * "borrowable". That order is not guaranteed anywhere, though, and if the
 * navigation were aborted and React wanted to reuse the same node, production
 * would show a `removeChild: node is not a child`. A clone of the live DOM is
 * pixel-identical (styles hang on classes, not on React), the scroll position
 * is written back by hand anyway, and the price is a single extra paint. The
 * clone is therefore the **normal case**, not the emergency exit.
 */

/** Rectangle as a plain data object — `DOMRect` doesn't survive sensibly as module state. */
export interface MorphRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface AssistantSurface {
    /** The surface itself (`.chatContent`). */
    node: HTMLElement;
    /** The visible conversation column (`.chatColumn`, max-w 1400) — without the dead margins. */
    column: () => HTMLElement | null;
    /** The area that actually scrolls (`.messages`). */
    scroller: () => HTMLElement | null;
    /** The displayed conversation — template for the panel preview (layer B). */
    conversation: () => unknown;
}

let surface: AssistantSurface | null = null;

/** Registers the chat surface; the return value unregisters it. */
export function registerAssistantSurface(next: AssistantSurface): () => void {
    surface = next;
    return () => {
        if (surface === next) surface = null;
    };
}

export function peekAssistantSurface(): AssistantSurface | null {
    return surface;
}

/** Tests only. */
export function resetAssistantSurface(): void {
    surface = null;
}

export interface SurfaceMeasurement {
    /** Start rectangle of the shell: the visible column. */
    originRect: MorphRect;
    /** Rectangle of the whole surface — gives the clone's position inside the shell. */
    surfaceRect: MorphRect;
    scrollTop: number;
    /**
     * The fill color **as a resolved pixel value**, not as `var(--oa-panel)`:
     * `AppShell` removes `chat-oa` from the `body` in the same commit in which
     * the flight starts — from that frame on the custom property would mean
     * something else and the shell would jump in color.
     */
    fill: string;
    /** Pixel-exact copy of the surface (layer A). */
    ghost: HTMLElement;
    /** The conversation for the panel preview (layer B). */
    conversation: unknown;
}

function toRect(rect: DOMRect): MorphRect {
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

/** Transparent — both mean "no color here". */
function isTransparent(color: string): boolean {
    return !color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)';
}

/**
 * The shell's fill color. `.chatContent` itself carries **no** background
 * (`App.sass` only sets width and flex there) — the visible chat background
 * sits on an ancestor (`bg-[var(--oa-panel)]` on the inset). So we walk up
 * from the surface until an opaque color appears; only at the very end does
 * white stand in as a last resort. A hard-coded `#ffffff` would happen to be
 * right today and, with the first brand using a different panel tone or a dark
 * mode, would be exactly the white frame this design rules out.
 */
function resolveFill(node: HTMLElement): string {
    try {
        let element: HTMLElement | null = node;
        while (element) {
            const color = window.getComputedStyle(element).backgroundColor;
            if (!isTransparent(color)) return color;
            element = element.parentElement;
        }
        const body = window.getComputedStyle(document.body).backgroundColor;
        if (!isTransparent(body)) return body;
    } catch {
        // jsdom and disabled styles: the last resort is below.
    }
    return '#ffffff';
}

/**
 * What `cloneNode` does **not** carry over: properties. React sets `value` as
 * a property, not as an attribute — so in the raw clone the half-typed input is
 * gone, and of all things the input row is the shared anchor of both worlds.
 * The same applies to `checked` and to anything painted into a `<canvas>`.
 */
function copyLiveValues(original: HTMLElement, ghost: HTMLElement): void {
    const fields = original.querySelectorAll<HTMLElement>('textarea, input');
    const twins = ghost.querySelectorAll<HTMLElement>('textarea, input');
    fields.forEach((field, index) => {
        const twin = twins[index];
        if (!twin) return;
        if (field instanceof HTMLTextAreaElement && twin instanceof HTMLTextAreaElement) {
            twin.value = field.value;
        } else if (field instanceof HTMLInputElement && twin instanceof HTMLInputElement) {
            twin.value = field.value;
            twin.checked = field.checked;
        }
    });

    const canvases = original.querySelectorAll('canvas');
    const canvasTwins = ghost.querySelectorAll('canvas');
    canvases.forEach((canvas, index) => {
        const twin = canvasTwins[index];
        if (!twin) return;
        try {
            twin.getContext('2d')?.drawImage(canvas, 0, 0);
        } catch {
            // An empty canvas is better than a click handler that throws.
        }
    });
}

/** This much above and below the visible area stays in the clone — one screen height each. */
const GHOST_KEEP_SCREENS = 1;

/**
 * **Cap for layer A.** The clone is created synchronously in the click handler
 * and attached in the first animation frame: a history with a few hundred
 * messages (Markdown, code blocks, resource cards) would land there as a full
 * layout plus paint in exactly the frame budget the "60 fps" requirement
 * depends on — and layer A is invisible after 300 ms anyway.
 *
 * So the clone keeps what can be visible in the first frames (visible area ±
 * one screen height); everything before and after is replaced by a placeholder
 * of **the same height**. Same height is the point: the scroll position written
 * back afterwards shows exactly the same spot. Reading happens in one go on the
 * real tree, writing exclusively on the clone (which hangs in no document, so it
 * costs no layout).
 */
function trimGhostTranscript(original: HTMLElement, ghost: HTMLElement): void {
    const scroller = original.querySelector<HTMLElement>('.messages');
    const list = scroller?.querySelector<HTMLElement>('.messagesList');
    const ghostList = ghost.querySelector<HTMLElement>('.messages .messagesList');
    if (!scroller || !list || !ghostList) return;

    const viewport = scroller.clientHeight;
    if (viewport <= 0) return;

    const keepFrom = scroller.scrollTop - viewport * GHOST_KEEP_SCREENS;
    const keepTo = scroller.scrollTop + viewport * (1 + GHOST_KEEP_SCREENS);

    const scrollerRect = scroller.getBoundingClientRect();
    const originals = Array.from(list.children) as HTMLElement[];
    const metrics = originals.map((child) => {
        const rect = child.getBoundingClientRect();
        return { start: rect.top - scrollerRect.top + scroller.scrollTop, height: rect.height };
    });

    const clones = Array.from(ghostList.children) as HTMLElement[];
    metrics.forEach((metric, index) => {
        const clone = clones[index];
        if (!clone) return;
        if (metric.start + metric.height >= keepFrom && metric.start <= keepTo) return;
        const spacer = ghostList.ownerDocument.createElement('div');
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.height = `${metric.height}px`;
        ghostList.replaceChild(spacer, clone);
    });
}

/**
 * Measures the surface in **one** pass (one forced layout, no read/write
 * chain). Returns `null` when nothing is registered or a rectangle stays empty
 * — then everything continues as before the morph. That is also exactly the
 * jsdom case: all rectangles are 0 there.
 */
export function measureAssistantSurface(): SurfaceMeasurement | null {
    const current = surface;
    if (!current || !current.node.isConnected) return null;

    const column = current.column() ?? current.node;
    const originRect = toRect(column.getBoundingClientRect());
    const surfaceRect = toRect(current.node.getBoundingClientRect());
    if (originRect.width <= 0 || originRect.height <= 0) return null;
    if (surfaceRect.width <= 0 || surfaceRect.height <= 0) return null;

    const scroller = current.scroller();
    const scrollTop = scroller ? scroller.scrollTop : 0;

    // A transparent background is no good as a cover: the shell must cover
    // from the first frame, otherwise the target page flashes through beneath.
    const fill = resolveFill(current.node);

    const ghost = current.node.cloneNode(true) as HTMLElement;
    // **Pin the dimensions.** In the real DOM `.chatContent` is a flex child of
    // `.chatBot`; `flex-basis: 0%` wins there over the legacy rule
    // `width: calc(100% - 250px)` (App.sass, from 1000 px). In the overlay the
    // clone hangs on a foreign parent; without fixed dimensions it would be
    // 250 px narrower and have `height: auto` — the text would reflow in the
    // first frame and the percentage chain down to `.messages` would not
    // resolve, so the scroll position would drop to 0.
    ghost.style.width = `${surfaceRect.width}px`;
    ghost.style.height = `${surfaceRect.height}px`;
    ghost.style.flex = 'none';
    copyLiveValues(current.node, ghost);
    trimGhostTranscript(current.node, ghost);

    return {
        originRect,
        surfaceRect,
        scrollTop,
        fill,
        ghost,
        conversation: current.conversation(),
    };
}

/** The scroller **inside** a clone — the clone carries the same classes. */
export function ghostScroller(ghost: HTMLElement): HTMLElement | null {
    return ghost.querySelector<HTMLElement>('.messages');
}
